import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { WizardErrorCallout, WizardHelpNote, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';

function defaultName(fullName, emailAddress) {
  if (fullName?.trim()) return fullName.trim();
  const email = emailAddress || '';
  const local = email.split('@')[0] || '';
  if (!local) return '';
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export function OnboardingProfile() {
  const navigate = useNavigate();
  const { profile, user, refreshMe } = useAuth();
  const [name, setName] = useState(() => defaultName(profile?.full_name, user?.email));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const next = defaultName(profile?.full_name, user?.email);
    if (next) setName(next);
  }, [profile?.full_name, user?.email]);

  const onContinue = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError(pt.onboarding.profile.nameError);
      return;
    }
    setLoading(true);
    setError(null);
    const profileResponse = await authAPI.patchProfile({ full_name: trimmed });
    if (!profileResponse.success) {
      setError(pt.auth.errors.generic);
      setLoading(false);
      return;
    }
    await authAPI.saveOnboardingStep('cycle_day');
    await refreshMe();
    setLoading(false);
    navigate('/onboarding/cycle-day');
  }, [name, navigate, refreshMe]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    loading,
  });

  return (
    <div className="flex flex-col gap-space-6">
      <WizardStepHeader
        eyebrow={pt.onboarding.profile.eyebrow}
        title={pt.onboarding.profile.title}
        subtitle={pt.onboarding.profile.subtitle}
      />

      <div className="onboarding-focus-ring flex flex-col gap-0.5 rounded-ds-lg px-space-4 pt-space-2-5 pb-space-3">
        <label htmlFor="onboarding-name" className="type-label font-semibold text-sage-700 dark:text-sage-300">
          {pt.onboarding.profile.fullNameLabel}
        </label>
        <input
          id="onboarding-name"
          type="text"
          autoComplete="given-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="type-title-page w-full border-none bg-transparent py-1 font-display font-semibold text-foreground outline-none"
        />
      </div>
      <WizardHelpNote tone="sage">{pt.onboarding.profile.nameHint}</WizardHelpNote>
      {error ? <WizardErrorCallout>{error}</WizardErrorCallout> : null}
    </div>
  );
}
